import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Check, X, Pencil, BookOpen, History, AlertTriangle, ChevronDown, ChevronRight, EyeOff, Eye, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { RateRollover } from "@/components/RateRollover";
import { fmtLog } from "@/lib/tz";

// Known NDIS rate tiers — must match billing_engine.py TIERS_DESC + special tiers
const KNOWN_TIERS = [
  "Day", "Evening", "Night", "Saturday", "Sunday", "PublicHoliday",
  "Sleepover", "Provider Travel", "Activity Based Transport",
  "Establishment Fee", "Weekly",
];

// Tier time-boundary reference shown in the New Item dialog
const TIER_REFERENCE = [
  { tier: "Day",                   when: "Mon–Fri · 6:00am – 8:00pm" },
  { tier: "Evening",               when: "Mon–Fri · 8:00pm – midnight" },
  { tier: "Night",                 when: "Mon–Fri · midnight – 6:00am" },
  { tier: "Saturday",              when: "All day Saturday" },
  { tier: "Sunday",                when: "All day Sunday" },
  { tier: "PublicHoliday",         when: "Public holidays (part-day: declared hours only)" },
  { tier: "Sleepover",             when: "Sleepover allowance (once per shift)" },
  { tier: "Provider Travel",       when: "Fixed unit charge — auto-applied per shift" },
  { tier: "Activity Based Transport", when: "Per-km charge on transport shifts" },
  { tier: "Establishment Fee",     when: "One-off establishment fee line" },
  { tier: "Weekly",                when: "SIL Weekly subscription billing" },
];

// ─── Price row (read + inline edit) ─────────────────────────────────────────
function PriceRow({ p, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ rate: p.price_list_rate, valid_from: p.valid_from || "", valid_upto: p.valid_upto || "" });

  const save = async () => {
    setBusy(true);
    try {
      await api.updatePricebookPrice(p.name, { rate: Number(f.rate), valid_from: f.valid_from, valid_upto: f.valid_upto });
      toast.success("Rate updated");
      setEdit(false);
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  if (!edit) return (
    <div className="flex items-center justify-between py-1.5 text-xs" data-testid={`pb-rate-row-${p.name}`}>
      <div className="flex items-center gap-2">
        <span className="font-semibold tabular-nums text-slate-800">${Number(p.price_list_rate).toFixed(2)}</span>
        {p.current && <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Current</span>}
      </div>
      <div className="flex items-center gap-3 text-slate-500">
        <span>{p.valid_from || "—"} → {p.valid_upto || "open"}</span>
        <button onClick={() => setEdit(true)} className="text-[#bf3b4f] hover:text-[#a32f42]" data-testid={`pb-rate-edit-${p.name}`}><Pencil className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-wrap items-end gap-2 py-2 border-t border-slate-100" data-testid={`pb-rate-form-${p.name}`}>
      <div><label className="text-[9px] text-slate-400">Rate ($)</label><Input type="number" step="0.01" value={f.rate} onChange={(e) => setF((s) => ({ ...s, rate: e.target.value }))} className="h-8 text-xs w-24" /></div>
      <div><label className="text-[9px] text-slate-400">Valid from</label><Input type="date" value={f.valid_from} onChange={(e) => setF((s) => ({ ...s, valid_from: e.target.value }))} className="h-8 text-xs w-36" /></div>
      <div><label className="text-[9px] text-slate-400">Valid upto</label><Input type="date" value={f.valid_upto} onChange={(e) => setF((s) => ({ ...s, valid_upto: e.target.value }))} className="h-8 text-xs w-36" /></div>
      <Button size="sm" className="h-8 text-xs" onClick={save} disabled={busy}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</Button>
      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEdit(false)}><X className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

// ─── Add new price row ────────────────────────────────────────────────────────
function AddPriceRow({ itemCode, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ rate: "", valid_from: "", valid_upto: "" });

  const save = async () => {
    if (f.rate === "") return toast.error("Enter a rate");
    setBusy(true);
    try {
      await api.addPricebookPrice({ item_code: itemCode, rate: Number(f.rate), valid_from: f.valid_from, valid_upto: f.valid_upto });
      toast.success("New rate added");
      setOpen(false);
      setF({ rate: "", valid_from: "", valid_upto: "" });
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add rate"); }
    finally { setBusy(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} className="text-[11px] text-[#bf3b4f] hover:text-[#a32f42] font-medium flex items-center gap-1 mt-1" data-testid={`pb-add-rate-${itemCode}`}>
      <Plus className="h-3 w-3" /> Add a new rate
    </button>
  );

  return (
    <div className="flex flex-wrap items-end gap-2 py-2 border-t border-slate-100 mt-1">
      <div><label className="text-[9px] text-slate-400">Rate ($)</label><Input type="number" step="0.01" value={f.rate} onChange={(e) => setF((s) => ({ ...s, rate: e.target.value }))} className="h-8 text-xs w-24" /></div>
      <div><label className="text-[9px] text-slate-400">Valid from</label><Input type="date" value={f.valid_from} onChange={(e) => setF((s) => ({ ...s, valid_from: e.target.value }))} className="h-8 text-xs w-36" /></div>
      <div><label className="text-[9px] text-slate-400">Valid upto</label><Input type="date" value={f.valid_upto} onChange={(e) => setF((s) => ({ ...s, valid_upto: e.target.value }))} className="h-8 text-xs w-36" /></div>
      <Button size="sm" className="h-8 text-xs" onClick={save} disabled={busy} style={{ backgroundColor: "#bf3b4f" }}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}</Button>
      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setOpen(false)}><X className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

// ─── Single item card ─────────────────────────────────────────────────────────
function ItemCard({ item, onReload }) {
  const [expanded, setExpanded] = useState(false);
  const [editName, setEditName] = useState(false);
  const [newName, setNewName] = useState(item.item_name);
  const [busy, setBusy] = useState(false);

  const saveName = async () => {
    if (!newName.trim()) return toast.error("Name cannot be empty");
    setBusy(true);
    try {
      await api.updatePricebookItem(item.item_code, { item_name: newName.trim() });
      toast.success("Item name updated");
      setEditName(false);
      await onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const toggleDisabled = async () => {
    setBusy(true);
    try {
      await api.updatePricebookItem(item.item_code, { disabled: item.disabled ? 0 : 1 });
      toast.success(item.disabled ? "Item re-enabled" : "Item disabled (stays in billing history)");
      await onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const currentPrice = item.prices?.find((p) => p.current);

  return (
    <div className={`rounded-md border p-3 ${item.disabled ? "border-slate-100 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`} data-testid={`pb-item-${item.item_code}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {editName ? (
            <div className="flex items-center gap-1.5">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="h-7 text-xs flex-1" autoFocus />
              <Button size="sm" className="h-7 px-2" onClick={saveName} disabled={busy}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}</Button>
              <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => { setEditName(false); setNewName(item.item_name); }}><X className="h-3 w-3" /></Button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-slate-800 truncate">{item.item_name}</span>
              <button onClick={() => setEditName(true)} className="text-slate-400 hover:text-[#bf3b4f] shrink-0" data-testid={`pb-edit-name-${item.item_code}`}><Pencil className="h-3 w-3" /></button>
            </div>
          )}
          {/* item_code, item_group, tier are LOCKED — read-only labels, never editable inputs */}
          <div className="text-[10px] text-slate-400 mt-0.5 flex flex-wrap items-center gap-x-2">
            <span className="font-mono">{item.item_code}</span>
            <span className="text-slate-300">·</span>
            <span>{item.item_group}</span>
            <span className="text-slate-300">·</span>
            <span className="italic">{item.custom_ndis_rate_tier || "—"}</span>
            {item.disabled ? <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 border border-slate-200 rounded-full px-1.5 py-0.5">Disabled</span> : null}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {currentPrice && <span className="text-xs font-semibold tabular-nums text-slate-700">${Number(currentPrice.price_list_rate).toFixed(2)}</span>}
          <button onClick={toggleDisabled} disabled={busy} title={item.disabled ? "Re-enable item" : "Disable item"}
            className="text-slate-400 hover:text-slate-600" data-testid={`pb-toggle-disabled-${item.item_code}`}>
            {item.disabled ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          </button>
          <button onClick={() => setExpanded((v) => !v)} className="text-slate-400 hover:text-slate-600" data-testid={`pb-expand-${item.item_code}`}>
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <div className="divide-y divide-slate-50">
            {(item.prices || []).map((p) => <PriceRow key={p.name} p={p} onSaved={onReload} />)}
            {(!item.prices || item.prices.length === 0) && <div className="text-[11px] text-slate-400 py-1">No price set yet.</div>}
          </div>
          <AddPriceRow itemCode={item.item_code} onSaved={onReload} />
        </div>
      )}
    </div>
  );
}


// ─── New item dialog ──────────────────────────────────────────────────────────
function NewItemDialog({ open, onOpenChange, groups, onCreated }) {
  const [f, setF] = useState({ item_code: "", item_name: "", item_group: "", custom_ndis_rate_tier: "", rate: "", valid_from: "" });
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState(null);

  const reset = () => {
    setF({ item_code: "", item_name: "", item_group: "", custom_ndis_rate_tier: "", rate: "", valid_from: "" });
    setWarning(null);
  };

  const save = async () => {
    setBusy(true);
    setWarning(null);
    try {
      const res = await api.createPricebookItem(f);
      if (res.warning) setWarning(res.warning);
      toast.success(`Item ${res.item?.item_code} created`);
      onCreated();
      if (!res.warning) { reset(); onOpenChange(false); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create item"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="pb-new-item-dialog">
        <DialogHeader><DialogTitle className="text-base">Add new pricebook item</DialogTitle></DialogHeader>
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 -mt-1">
          ⚠ Item code, group and tier are <strong>permanent</strong> once created — they are the keys the billing engine uses for all invoiced shifts and cannot be changed.
        </p>

        {warning && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800" data-testid="pb-new-item-warning">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
            <span>{warning}</span>
          </div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-slate-600">Item code <span className="text-rose-500">*</span></label>
              <Input value={f.item_code} onChange={(e) => setF((s) => ({ ...s, item_code: e.target.value.toUpperCase() }))}
                className="h-9 text-xs mt-1 font-mono" placeholder="e.g. 01_801_0138_1_1" data-testid="pb-new-item-code" />
              <p className="text-[9px] text-slate-400 mt-0.5">Permanent · matches NDIS price guide</p>
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-600">Item name <span className="text-rose-500">*</span></label>
              <Input value={f.item_name} onChange={(e) => setF((s) => ({ ...s, item_name: e.target.value }))}
                className="h-9 text-xs mt-1" placeholder="e.g. Weekday - Day Rate" data-testid="pb-new-item-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-slate-600">Item group (billing type) <span className="text-rose-500">*</span></label>
              <select value={f.item_group} onChange={(e) => setF((s) => ({ ...s, item_group: e.target.value }))}
                className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-xs mt-1" data-testid="pb-new-item-group">
                <option value="">— select —</option>
                {groups.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <p className="text-[9px] text-slate-400 mt-0.5">Permanent · billing groups by this</p>
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-600">NDIS rate tier <span className="text-rose-500">*</span></label>
              <select value={f.custom_ndis_rate_tier} onChange={(e) => setF((s) => ({ ...s, custom_ndis_rate_tier: e.target.value }))}
                className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-xs mt-1" data-testid="pb-new-item-tier">
                <option value="">— select —</option>
                {KNOWN_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <p className="text-[9px] text-slate-400 mt-0.5">Permanent · billing resolves rate by tier</p>
              {f.custom_ndis_rate_tier && (
                <p className="text-[10px] text-slate-500 mt-1 bg-slate-50 border border-slate-100 rounded px-2 py-1">
                  {TIER_REFERENCE.find((r) => r.tier === f.custom_ndis_rate_tier)?.when || ""}
                </p>
              )}
            </div>
          </div>
          <div className="border-t border-slate-100 pt-3">
            <div className="text-[11px] font-medium text-slate-600 mb-2">Initial price <span className="text-slate-400 font-normal">(optional — can be added later)</span></div>
            <div className="flex flex-wrap gap-3">
              <div>
                <label className="text-[9px] text-slate-400">Rate ($)</label>
                <Input type="number" step="0.01" value={f.rate} onChange={(e) => setF((s) => ({ ...s, rate: e.target.value }))}
                  className="h-8 text-xs w-24 mt-0.5" data-testid="pb-new-item-rate" />
              </div>
              <div>
                <label className="text-[9px] text-slate-400">Valid from</label>
                <Input type="date" value={f.valid_from} onChange={(e) => setF((s) => ({ ...s, valid_from: e.target.value }))}
                  className="h-8 text-xs w-36 mt-0.5" data-testid="pb-new-item-valid-from" />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-slate-100 pt-3">
          <Button variant="outline" size="sm" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy} style={{ backgroundColor: "#bf3b4f" }} data-testid="pb-new-item-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Group row with archive controls ─────────────────────────────────────────
function GroupRow({ g, canEdit, onReload }) {
  const [archiving, setArchiving] = useState(false);
  const [archiveDate, setArchiveDate] = useState("");
  const [busy, setBusy] = useState(false);

  const doArchive = async () => {
    if (!archiveDate) return toast.error("Pick an archive date");
    setBusy(true);
    try {
      await api.archivePricebookGroup(g.name, { archived_from: archiveDate });
      toast.success(`"${g.name}" archived from ${archiveDate}`);
      setArchiving(false);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
    finally { setBusy(false); }
  };

  const doRestore = async () => {
    setBusy(true);
    try {
      await api.archivePricebookGroup(g.name, { archived_from: null });
      toast.success(`"${g.name}" restored`);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
    finally { setBusy(false); }
  };

  const isArchived = !!g.archived_from;

  return (
    <div className={`border-b border-slate-50 py-1.5 ${isArchived ? "opacity-60" : ""}`}
         data-testid={`pb-group-row-${g.name}`}>
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className={g.is_group ? "text-slate-400 italic truncate" : "text-slate-700 truncate"}>{g.name}</span>
          {g.is_group && <span className="text-[9px] text-slate-400 bg-slate-100 rounded px-1 shrink-0">parent</span>}
          {isArchived && (
            <span className="text-[9px] font-bold uppercase tracking-wider text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-1.5 py-0.5 shrink-0">
              Archived · from {g.archived_from}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] text-slate-400">{g.item_count} item{g.item_count !== 1 ? "s" : ""}</span>
          {canEdit && !g.is_group && (
            isArchived ? (
              <button onClick={doRestore} disabled={busy} title="Restore billing type"
                className="text-slate-400 hover:text-emerald-600" data-testid={`pb-group-restore-${g.name}`}>
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button onClick={() => setArchiving((v) => !v)} title="Archive billing type"
                className="text-slate-400 hover:text-rose-500" data-testid={`pb-group-archive-${g.name}`}>
                <Archive className="h-3.5 w-3.5" />
              </button>
            )
          )}
        </div>
      </div>

      {archiving && !isArchived && (
        <div className="mt-2 p-2 bg-rose-50 border border-rose-100 rounded-md">
          <p className="text-[11px] text-rose-700 mb-2">
            <strong>Archive "{g.name}"</strong> — after this date it will be hidden from shift creation and participant setup.
            Historical shifts using this type before the date are completely unaffected.
          </p>
          <div className="flex items-end gap-2">
            <div>
              <label className="text-[10px] font-medium text-slate-600">Archive from date <span className="text-rose-500">*</span></label>
              <Input type="date" value={archiveDate} onChange={(e) => setArchiveDate(e.target.value)}
                className="h-8 text-xs mt-0.5 w-36" data-testid={`pb-group-archive-date-${g.name}`} />
            </div>
            <Button size="sm" className="h-8 text-xs" onClick={doArchive} disabled={busy}
              style={{ backgroundColor: "#bf3b4f" }} data-testid={`pb-group-archive-confirm-${g.name}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Archive className="h-3 w-3 mr-1" />Archive</>}
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setArchiving(false)}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}



// ─── Main PricebookManager component ─────────────────────────────────────────
export function PricebookManager({ canEdit }) {
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [audit, setAudit] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [showDisabled, setShowDisabled] = useState(false);
  const [search, setSearch] = useState("");
  const [newItemOpen, setNewItemOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [addingGroup, setAddingGroup] = useState(false);
  const [showGroups, setShowGroups] = useState(false);

  const load = () => {
    api.pricebookItems().then(setData).catch(() => toast.error("Could not load pricebook"));
    api.pricebookGroups().then(setGroups).catch(() => {});
    api.rateAudit().then(setAudit).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const leafGroups = groups.filter((g) => !g.is_group).map((g) => g.name).sort();

  const saveGroup = async () => {
    if (!newGroupName.trim()) return toast.error("Enter a group name");
    setAddingGroup(true);
    try {
      await api.createPricebookGroup({ name: newGroupName.trim() });
      toast.success("Billing type created");
      setNewGroupName("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create group"); }
    finally { setAddingGroup(false); }
  };

  const items = data?.items || [];
  const q = search.trim().toLowerCase();
  const visible = items.filter((i) => {
    if (!showDisabled && i.disabled) return false;
    if (!q) return true;
    return (i.item_name || "").toLowerCase().includes(q) ||
           (i.item_code || "").toLowerCase().includes(q) ||
           (i.item_group || "").toLowerCase().includes(q) ||
           (i.custom_ndis_rate_tier || "").toLowerCase().includes(q);
  });
  const byGroup = visible.reduce((m, i) => {
    const g = i.item_group || "—";
    (m[g] = m[g] || []).push(i);
    return m;
  }, {});

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3" data-testid="pricebook-manager">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1.5">
            <BookOpen className="h-3 w-3" />Pricebook &amp; Item Prices
          </div>
          <p className="text-[11px] text-slate-500">
            NDIS line items and their Standard Selling prices. Item code, group and tier are{" "}
            <span className="font-semibold">permanent once created</span> — billing engine keys.
            Use <span className="font-semibold">FY rate update</span> for bulk changes, or expand an item to add/edit dated rates.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canEdit && <RateRollover onDone={load} />}
          {canEdit && (
            <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => setNewItemOpen(true)} data-testid="pb-add-item-btn">
              <Plus className="h-3.5 w-3.5 mr-1.5" />New item
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items…"
          className="h-8 text-xs max-w-64" data-testid="pb-search" />
        <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none">
          <input type="checkbox" checked={showDisabled} onChange={(e) => setShowDisabled(e.target.checked)}
            className="rounded border-slate-300" data-testid="pb-show-disabled" />
          Show disabled
        </label>
        {data && <span className="text-[11px] text-slate-400 ml-auto">{visible.length} item{visible.length !== 1 ? "s" : ""}</span>}
      </div>

      {/* ── Billing Types (Item Groups) section ─────────────────────────── */}
      <div className="border border-slate-100 rounded-md mb-4">
        <button onClick={() => setShowGroups((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 rounded-md"
          data-testid="pb-groups-toggle">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Billing Types</span>
            <span className="text-[10px] text-slate-400">{leafGroups.length} type{leafGroups.length !== 1 ? "s" : ""}</span>
          </div>
          {showGroups ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
        </button>

        {showGroups && (
          <div className="px-3 pb-3 border-t border-slate-100">
            <p className="text-[11px] text-slate-500 mt-2 mb-2">
              Billing types are the NDIS support categories assigned to shifts and participants.
              The name is <span className="font-semibold">permanent</span> — it is stored on every shift that uses this type.
            </p>
            <div className="space-y-1 mb-3">
              {groups.map((g) => (
                <GroupRow key={g.name} g={g} canEdit={canEdit} onReload={load} />
              ))}
            </div>
            {canEdit && (
              <div className="flex items-end gap-2 pt-2 border-t border-slate-100">
                <div className="flex-1">
                  <label className="text-[10px] font-medium text-slate-600">New billing type name <span className="text-rose-500">*</span></label>
                  <Input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveGroup()}
                    className="h-8 text-xs mt-0.5" placeholder="e.g. 01 Assistance With Self Care - Standard"
                    data-testid="pb-new-group-name" />
                  <p className="text-[9px] text-slate-400 mt-0.5">Permanent — must match the NDIS support category exactly</p>
                </div>
                <Button size="sm" className="h-8 text-xs shrink-0" onClick={saveGroup} disabled={addingGroup}
                  style={{ backgroundColor: "#bf3b4f" }} data-testid="pb-new-group-save">
                  {addingGroup ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3 w-3 mr-1" />Add</>}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Line Items & Prices ──────────────────────────────────────────── */}
      {!data ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : visible.length === 0 ? (
        <div className="text-xs text-slate-400 py-6 text-center">
          {items.length === 0 ? "No pricebook items. Use \"New item\" to create the first one." : "No items match your search."}
        </div>
      ) : (
        Object.keys(byGroup).sort().map((group) => (
          <div key={group} className="mb-4" data-testid={`pb-group-${group}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 sticky top-0 bg-white py-0.5 z-10">{group}</div>
            <div className="space-y-2">
              {byGroup[group].map((item) => <ItemCard key={item.item_code} item={item} onReload={load} />)}
            </div>
          </div>
        ))
      )}

      <div className="mt-4 border-t border-slate-100 pt-3">
        <button onClick={() => setShowAudit((v) => !v)}
          className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="pb-audit-toggle">
          <History className="h-3 w-3" />{showAudit ? "Hide" : "Show"} rate change history{audit ? ` (${audit.length})` : ""}
        </button>
        {showAudit && (
          <div className="mt-2 space-y-1" data-testid="pb-audit-list">
            {!audit || audit.length === 0 ? (
              <div className="text-[11px] text-slate-400">No rate changes logged yet.</div>
            ) : audit.map((a) => (
              <div key={a.name} className="flex items-center gap-2 text-[11px] py-1 border-b border-slate-50">
                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 w-16">{a.action}</span>
                <span className="flex-1 min-w-0 truncate text-slate-700">{a.item_name || a.item_code}</span>
                <span className="tabular-nums text-slate-500">{a.old_rate != null ? `$${Number(a.old_rate).toFixed(2)}` : "—"} → <b className="text-slate-800">${Number(a.new_rate).toFixed(2)}</b></span>
                <span className="text-[10px] text-slate-400 shrink-0">{a.source || ""}</span>
                <span className="text-slate-400 w-40 text-right truncate">{a.changed_by} · {fmtLog(a.changed_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {canEdit && <NewItemDialog open={newItemOpen} onOpenChange={setNewItemOpen} groups={leafGroups} onCreated={load} />}
    </div>
  );
}

